<script setup lang="ts">
/**
 * 我的画像。
 *
 * 一个刻意的行为：**服务端的画像会整份覆盖本地表单**。
 * `/auth/me` 在登录后会重新拉取，如果这时只做"缺省值填充"，
 * 用户刚在这台设备上改过的、还没保存的内容会看起来"保存成功了"。
 * 服务端才是事实来源，覆盖虽然粗暴，但不会说谎。
 *
 * `dirty` 的比较是**顺序敏感**的：契约里 `childAgeBands` / `concerns` 都是数组，
 * 服务端按固定顺序回传。用集合比较会把"顺序变了"当成没改，
 * 而顺序在契约里是有意义的（AGE_BANDS / DOMAINS 的声明顺序）。
 */

import { computed, ref, watch } from 'vue';
import {
  DOMAINS,
  DOMAIN_META,
  ProfileResponseSchema,
  type AgeBand,
  type Domain,
  type PreferredTone,
  type ProfileResponse,
} from '@spirit/contracts';

import AgeBandPicker from '../components/chat/AgeBandPicker.vue';
import Button from '../components/ui/Button.vue';
import { pushToast } from '../components/ui/toast-store';
import { apiRequest, isApiError } from '../lib/api-client';
import { applyProfile, useAuth } from '../lib/auth-store';
import { cn } from '../lib/cn';

const TONE_OPTIONS: ReadonlyArray<{ value: PreferredTone; label: string; hint: string }> = [
  { value: 'gentle', label: '温和共情', hint: '先接住情绪，再谈做法' },
  { value: 'balanced', label: '平衡', hint: '看情况在两者之间（默认）' },
  { value: 'firm', label: '明确坚定', hint: '直接给边界和规则' },
];

const CHILD_COUNT_OPTIONS: ReadonlyArray<number> = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const auth = useAuth();

const ageBands = ref<AgeBand[]>([]);
const childCount = ref(0);
const concerns = ref<Domain[]>([]);
const tone = ref<PreferredTone>('balanced');
const saving = ref(false);
const error = ref<string | null>(null);

watch(
  () => auth.profile,
  (profile) => {
    if (profile === null) return;
    ageBands.value = [...profile.childAgeBands];
    childCount.value = profile.childCount;
    concerns.value = [...profile.concerns];
    tone.value = profile.preferredTone;
  },
  { immediate: true },
);

const dirty = computed(() => {
  const profile = auth.profile;
  if (profile === null) return true;
  if (ageBands.value.length !== profile.childAgeBands.length) return true;
  for (let index = 0; index < ageBands.value.length; index += 1) {
    if (ageBands.value[index] !== profile.childAgeBands[index]) return true;
  }
  if (childCount.value !== profile.childCount) return true;
  if (concerns.value.length !== profile.concerns.length) return true;
  for (let index = 0; index < concerns.value.length; index += 1) {
    if (concerns.value[index] !== profile.concerns[index]) return true;
  }
  return tone.value !== profile.preferredTone;
});

function toggleConcern(domain: Domain): void {
  // 用 DOMAINS.filter 而不是 push：结果永远按契约声明顺序排列。
  const next = DOMAINS.filter((item) => concerns.value.includes(item) || item === domain);
  concerns.value = [...next];
}

async function handleSave(): Promise<void> {
  saving.value = true;
  error.value = null;
  try {
    // PUT 是覆盖语义；UpdateProfileSchema 是 .strict()，多一个字段就是 40001。
    const result = await apiRequest<ProfileResponse>('/profile', ProfileResponseSchema, {
      method: 'PUT',
      body: {
        childAgeBands: ageBands.value,
        childCount: childCount.value,
        concerns: concerns.value,
        preferredTone: tone.value,
      },
    });
    applyProfile(result.profile);
    pushToast({ tone: 'success', title: '画像已保存' });
  } catch (caught) {
    const message = isApiError(caught) ? caught.message : '保存失败，请稍后重试';
    error.value = message;
    pushToast({ tone: 'danger', title: '保存失败', description: message });
  } finally {
    saving.value = false;
  }
}

const needsLogin = computed(() => auth.hydrated && auth.status !== 'authenticated');
const statusText = computed(() =>
  dirty.value ? '有未保存的修改' : auth.profile === null ? '尚未保存过画像' : '已与服务器同步',
);
</script>

<template>
  <div v-if="needsLogin" class="mx-auto w-full max-w-2xl px-4 py-12 text-center">
    <h1 class="text-lg font-semibold text-slate-900">登录后设置画像</h1>
    <p class="mt-2 text-sm text-slate-600">画像保存在你的账号下，换设备也能继续用。</p>
    <RouterLink to="/login" class="mt-6 inline-block"><Button>去登录</Button></RouterLink>
  </div>

  <div v-else class="mx-auto w-full max-w-2xl px-4 py-6">
    <h1 class="text-xl font-semibold text-slate-900">我的画像</h1>
    <p class="mt-1.5 text-sm leading-relaxed text-slate-600">
      画像会影响三件事：回答里的年龄段假设、默认关注的领域、以及沟通语气。
      留空也能正常咨询，服务端会按提问内容自行判断。
    </p>

    <section class="mt-6 rounded-xl border border-slate-200 bg-white p-4">
      <h2 class="text-sm font-medium text-slate-900">孩子年龄段</h2>
      <div class="mt-2">
        <AgeBandPicker
          :value="ageBands"
          :max="4"
          id-prefix="profile-age-band"
          @change="(next) => (ageBands = next)"
        />
      </div>
    </section>

    <section class="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <h2 class="text-sm font-medium text-slate-900">子女数量</h2>
      <label class="mt-3 flex items-center gap-3 text-sm text-slate-700">
        <span class="sr-only">子女数量</span>
        <select
          :value="childCount"
          class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          @change="(event) => (childCount = Number((event.target as HTMLSelectElement).value))"
        >
          <option v-for="count in CHILD_COUNT_OPTIONS" :key="count" :value="count">
            {{ count === 0 ? '暂不填写' : `${count} 个` }}
          </option>
        </select>
        <span class="text-xs text-slate-500">
          {{ childCount === 0 ? '契约用 0 表示未填写' : '会用于判断建议的分配方式' }}
        </span>
      </label>
    </section>

    <section class="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <h2 class="text-sm font-medium text-slate-900">关注的领域</h2>
      <div class="mt-3 flex flex-wrap gap-2" role="group" aria-label="关注的领域">
        <button
          v-for="domain in DOMAINS"
          :key="domain"
          type="button"
          :aria-pressed="concerns.includes(domain) ? 'true' : 'false'"
          :class="
            cn(
              'rounded-full border px-3 py-1.5 text-sm transition-colors',
              concerns.includes(domain)
                ? 'border-brand-500 bg-brand-500 text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:bg-brand-50',
            )
          "
          @click="toggleConcern(domain)"
        >
          {{ DOMAIN_META[domain].label }}
        </button>
      </div>
    </section>

    <fieldset class="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <legend class="text-sm font-medium text-slate-900">沟通语气</legend>
      <div class="mt-3 space-y-2">
        <label
          v-for="option in TONE_OPTIONS"
          :key="option.value"
          :class="
            cn(
              'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
              tone === option.value ? 'border-brand-500 bg-brand-50/40' : 'border-slate-200 hover:bg-slate-50',
            )
          "
        >
          <input
            type="radio"
            name="preferredTone"
            class="mt-0.5"
            :value="option.value"
            :checked="tone === option.value"
            @change="() => (tone = option.value)"
          />
          <span>
            <span class="block text-sm font-medium text-slate-900">{{ option.label }}</span>
            <span class="mt-0.5 block text-xs text-slate-500">{{ option.hint }}</span>
          </span>
        </label>
      </div>
    </fieldset>

    <p
      v-if="error !== null"
      role="alert"
      class="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
    >
      {{ error }}
    </p>

    <div class="sticky bottom-0 mt-6 flex items-center justify-between gap-3 border-t border-slate-200 bg-[var(--bg)] py-3">
      <span class="text-xs text-slate-500">{{ statusText }}</span>
      <Button :disabled="saving || !dirty" @click="() => void handleSave()">
        {{ saving ? '保存中…' : '保存画像' }}
      </Button>
    </div>
  </div>
</template>
